import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, UserPlus, AlertCircle, Clock } from 'lucide-react';

export default function LoginPage() {
  const { user, loading, login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-pulse text-slate-400 font-medium">Loading...</div>
    </div>
  );
  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    let result;
    if (isRegister) {
      result = await register(name, email, password, mobile);
      if (result.success && result.pending) {
        setPendingMessage(result.message || 'Registration submitted. Your account is pending admin approval.');
        setSubmitting(false);
        return;
      }
    } else {
      result = await login(email, password);
    }
    if (!result.success) setError(result.error);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1769298084996-8ed5d3a72870?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjaXR5JTIwc2t5bGluZSUyMGFlcmlhbHxlbnwwfHx8fDE3NzYzMzQ2NTN8MA&ixlib=rb-4.1.0&q=85"
          alt="City"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 to-slate-900/80" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-6">
            <img src="/favicon.png" alt="MuniCop" className="w-10 h-10 object-contain" />
            <span className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>MuniCop</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-4" style={{ fontFamily: 'Outfit' }}>
            Street Vendor<br />Enforcement System
          </h1>
          <p className="text-lg text-blue-100/80 max-w-md">
            Streamlined challan management for municipal officers. Issue penalties, track payments, and maintain records digitally.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <img src="/favicon.png" alt="MuniCop" className="w-8 h-8 object-contain" />
            <span className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit' }}>MuniCop</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
            {isRegister ? 'Create Account' : 'Officer Login'}
          </h2>
          <p className="text-slate-500 mb-8">
            {isRegister ? 'Register as a new municipal officer' : 'Sign in to access the enforcement dashboard'}
          </p>

          {pendingMessage && (
            <div className="flex items-start gap-3 p-4 mb-6 bg-amber-50 border border-amber-200 rounded-md text-amber-800" data-testid="pending-message">
              <Clock className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-semibold text-sm">Awaiting Approval</p>
                <p className="text-sm mt-0.5">{pendingMessage}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 mb-6 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-slate-700 font-medium text-sm">Full Name</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  className="mt-1.5 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent h-11"
                />
              </div>
            )}
            {isRegister && (
              <div>
                <Label htmlFor="mobile" className="text-slate-700 font-medium text-sm">Mobile Number</Label>
                <Input
                  id="mobile"
                  type="tel"
                  data-testid="register-mobile-input"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  className="mt-1.5 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent h-11"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="officer@municorp.com"
                required
                className="mt-1.5 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium text-sm">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="mt-1.5 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={submitting}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold tracking-wide transition-all duration-200"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isRegister ? 'Creating...' : 'Signing in...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isRegister ? <UserPlus className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
                  {isRegister ? 'Create Account' : 'Sign In'}
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              {isRegister ? 'Already have an account? Sign in' : 'Need an account? Register'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
